import React, { useEffect, useState, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import { Box, IconButton, Tooltip, Typography, Badge } from '@mui/material';
import { useRecoilState, useSetRecoilState, useRecoilValue, useRecoilCallback, selector } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AccessibilityControls } from '@app/shared/components/accessibility/AccessibilityControls';
import { SavedButton } from './SavedButton';
import { atom_tourNotifications } from '@app/shared/state/notification.atom';
import { service_tourService } from '@app/shared/services/tours.service';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';
import { Z_INDEX } from '@app/shared/styles/zIndex';

// Home icon (filled) - for selected state
const HomeIcon = ({ color = '#D0552E' }: { color?: string }) => (
    <svg style={{ width: 22, height: 22, minWidth: 22, minHeight: 22 }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.8632 6.18626L11.9332 0.646255C10.8632 -0.213745 9.13319 -0.213745 8.07319 0.636256L1.14319 6.18626C0.363195 6.80626 -0.136805 8.11625 0.0331948 9.09626L1.36319 17.0563C1.60319 18.4763 2.96319 19.6263 4.40319 19.6263H15.6032C17.0332 19.6263 18.4032 18.4663 18.6432 17.0563L19.9732 9.09626C20.1332 8.11625 19.6332 6.80626 18.8632 6.18626ZM10.0032 13.3163C8.62319 13.3163 7.50319 12.1963 7.50319 10.8163C7.50319 9.43626 8.62319 8.31626 10.0032 8.31626C11.3832 8.31626 12.5032 9.43626 12.5032 10.8163C12.5032 12.1963 11.3832 13.3163 10.0032 13.3163Z" fill={color} />
    </svg>
);

// Empty Home icon (outline) - for unselected state
const EmptyHomeIcon = ({ color = '#4F4F4F' }: { color?: string }) => (
    <svg style={{ width: 22, height: 22, minWidth: 22, minHeight: 22 }} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 18V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10.07 2.81985L3.14 8.36985C2.36 8.98985 1.86 10.2998 2.03 11.2798L3.36 19.2398C3.6 20.6598 4.96 21.8098 6.4 21.8098H17.6C19.03 21.8098 20.4 20.6498 20.64 19.2398L21.97 11.2798C22.13 10.2998 21.63 8.98985 20.86 8.36985L13.93 2.82985C12.86 1.96985 11.13 1.96985 10.07 2.81985Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
import { atom_tourState, atom_originalTourState, atom_isDesktop, atom_showSmartAgent } from '@app/shared/state/tour.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { useRenderTracker } from '@app/shared/debug';
import { atom_showModalContent, atom_modalType, atom_plannedRoute } from '@app/shared/state/show-content-modal.atom';
import type { PlannedRoute } from '@app/shared/models/tour.type';
import type { SideBarState } from '@app/shared/state/sidebar.atom';

// Selector: only re-renders SideBar when config/id change, not when stops/title change
const selector_sidebarTourConfig = selector({
    key: 'sidebarTourConfig',
    get: ({ get }) => {
        const tour = get(atom_tourState);
        return {
            id: tour?.id,
            mainColor: tour?.config?.mainColor,
            sidebarColor: tour?.config?.sidebarColor,
        };
    },
});

// Re-export for backward compatibility
export { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
export type { SideBarState } from '@app/shared/state/sidebar.atom';

interface SideBarProps {
    onStateChange?: (state: SideBarState) => void;
}

// Default tour color fallback
const DEFAULT_TOUR_COLOR = '#D0552E';

// Determine if a hex color is dark (returns true) or light (returns false)
function isDarkColor(hex: string): boolean {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    // Relative luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
}

export const SideBar = memo(function SideBar({ onStateChange }: SideBarProps) {
    useRenderTracker('SideBar');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeState, setActiveState] = useRecoilState(sideBarStateAtom);
    const componentConfig = useRecoilValue(selector_sidebarTourConfig);
    const [originalTour, setOriginalTour] = useRecoilState(atom_originalTourState);
    const [tourStage, setTourStage] = useRecoilState(atom_tourStage);
    const [listMode, setListMode] = useRecoilState(atom_listModeState);
    const isDesktop = useRecoilValue(atom_isDesktop);
    const [showSmartAgent, setShowSmartAgent] = useRecoilState(atom_showSmartAgent);
    const setShowModal = useSetRecoilState(atom_showModalContent);
    const setModalType = useSetRecoilState(atom_modalType);
    const setPlannedRoute = useSetRecoilState(atom_plannedRoute);

    // Mobile "More" button expansion state
    const [moreExpanded, setMoreExpanded] = useState(false);

    // Sync sidebar active state with list mode
    const prevListModeRef = useRef(listMode);
    useEffect(() => {
        const prev = prevListModeRef.current;
        prevListModeRef.current = listMode;

        if (!activeState) return;
        const defaultModes = ['mobileList', 'mobileSwiper', 'desktop', null];
        const sidebarOwnedModes = ['mobileAddPlace', 'desktopAddPlace', 'desktopNotifications'];

        if (defaultModes.includes(listMode)) {
            if (sidebarOwnedModes.includes(prev as string)) {
                // Coming back from a sidebar-owned mode → only clear if activeState still matches that mode
                const modeToState: Record<string, string> = {
                    mobileAddPlace: 'addPlace',
                    desktopAddPlace: 'addPlace',
                    desktopNotifications: 'notifications',
                };
                const expectedState = modeToState[prev as string];
                if (activeState === expectedState) {
                    setActiveState(null);
                }
            } else if (prev && !defaultModes.includes(prev)) {
                // Coming back from a temporary mode (navigation, route, filter) → restore sidebar list mode
                const sidebarToListMode: Record<string, string> = {
                    addPlace: isDesktop ? 'desktopAddPlace' : 'mobileAddPlace',
                    saved: 'mobileList',
                    notifications: 'desktopNotifications',
                };
                const targetMode = sidebarToListMode[activeState];
                if (targetMode) {
                    setListMode(targetMode as any);
                }
            }
        }
    }, [listMode]);

    // RTL detection
    const isRtl = document.dir === 'rtl';

    // Get tour color dynamically
    const tourColor = componentConfig.mainColor || DEFAULT_TOUR_COLOR;

    // Sidebar background color and adaptive text/icon color
    const sidebarBg = componentConfig.sidebarColor ? `#${componentConfig.sidebarColor.replace('#', '')}` : 'white';
    const sidebarIsDark = sidebarBg !== 'white' && isDarkColor(sidebarBg);
    const sidebarTextColor = sidebarIsDark ? '#fff' : '#666';
    const sidebarMenuTextColor = sidebarIsDark ? '#fff' : '#333';

    // Notifications animation and unread count - only active when tourStage is 'map'
    const notifications = useRecoilValue(atom_tourNotifications);

    // Only show notifications when tourStage is 'map'
    const isMapStage = tourStage === 'map';

    // Notifications that are in the list (showInList && seen) - with safety check
    const listNotifications = (isMapStage && notifications && Array.isArray(notifications))
        ? notifications.filter(n => n.showInList && n.userState?.seen)
        : [];

    // Unread = in list but not yet seenOnList (backend tracks this)
    const unreadNotificationsCount = listNotifications.filter(n =>
        !n.userState?.seenOnList
    ).length;

    const [animateNotifications, setAnimateNotifications] = useState(false);
    const prevUnreadCountRef = useRef(unreadNotificationsCount);

    // Animation effect when unread count increases (new notification entered list)
    useEffect(() => {
        if (unreadNotificationsCount > prevUnreadCountRef.current) {
            setAnimateNotifications(true);
            const timeout = setTimeout(() => {
                setAnimateNotifications(false);
            }, 600);
            prevUnreadCountRef.current = unreadNotificationsCount;
            return () => clearTimeout(timeout);
        }
        prevUnreadCountRef.current = unreadNotificationsCount;
    }, [unreadNotificationsCount]);

    // My Tours badge state — restore from localStorage so badge survives refresh
    const [newToursCount, setNewToursCount] = useState(() => {
        try {
            const raw = localStorage.getItem('credential_items');
            const uID = raw ? JSON.parse(raw)?.uID : null;
            if (!uID) return 0;
            const stored = localStorage.getItem(`my_tours_new_count_${uID}`);
            return stored ? parseInt(stored, 10) || 0 : 0;
        } catch { return 0; }
    });
    const prevNewToursCountRef = useRef(newToursCount);
    const lastTourCheckRef = useRef(Date.now());

    // Persist newToursCount to localStorage whenever it changes
    useEffect(() => {
        try {
            const raw = localStorage.getItem('credential_items');
            const uID = raw ? JSON.parse(raw)?.uID : null;
            if (!uID) return;
            if (newToursCount > 0) {
                localStorage.setItem(`my_tours_new_count_${uID}`, String(newToursCount));
            } else {
                localStorage.removeItem(`my_tours_new_count_${uID}`);
            }
        } catch { /* ignore */ }
    }, [newToursCount]);

    // Check for new tours on visibilitychange (when returning to the app)
    useEffect(() => {
        if (!isMapStage) return;

        const handleVisibilityChange = async () => {
            if (document.visibilityState !== 'visible') return;

            // Throttle: 30s minimum between checks
            const now = Date.now();
            if (now - lastTourCheckRef.current < 30000) return;
            lastTourCheckRef.current = now;

            const raw = localStorage.getItem('credential_items');
            const credentials = raw ? JSON.parse(raw) : {};
            const uID = credentials?.uID;
            if (!uID) return;

            const knownCountStr = localStorage.getItem(`my_tours_known_count_${uID}`);
            const cacheKey = `my_tours_cache_${uID}`;

            // Read cached tour IDs (needed for activeTour check)
            let oldCachedTourIds: string[] = [];
            const oldCachedRaw = localStorage.getItem(cacheKey);
            if (oldCachedRaw) {
                try {
                    const parsed = JSON.parse(oldCachedRaw);
                    oldCachedTourIds = (parsed?.tours || []).map((t: any) => String(t.tourId || t.id));
                } catch (e) { /* ignore */ }
            }

            // First time: initialize baseline from cache, no badge
            if (!knownCountStr) {
                let baseline = oldCachedTourIds.length;
                // If current tour isn't in the cache, count it as known
                const activeTourId = componentConfig.id;
                if (activeTourId && !oldCachedTourIds.includes(String(activeTourId))) {
                    baseline += 1;
                }
                localStorage.setItem(`my_tours_known_count_${uID}`, String(baseline));
                return;
            }

            try {
                const data = await service_tourService.getUserTours(uID);
                const freshTours = Array.isArray(data) ? data : (data?.tours || []);
                const freshCount = freshTours.length;

                const knownCount = parseInt(knownCountStr, 10) || 0;

                // If activeTour is NOT in the old cached tours list,
                // it means the user started this tour without visiting MyToursPage,
                // so don't count it as "new" — add +1 to the baseline
                const activeTourId = componentConfig.id;
                let effectiveKnownCount = knownCount;
                if (activeTourId && !oldCachedTourIds.includes(String(activeTourId))) {
                    effectiveKnownCount += 1;
                }

                const diff = freshCount - effectiveKnownCount;
                setNewToursCount(diff > 0 ? diff : 0);
            } catch (e) {
                // Silently fail - no badge on error
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isMapStage, componentConfig.id]);

    // Close accessibility modal if it's open
    const closeAccessibilityModal = () => {
        setShowModal(false);
        setModalType(null);
    };

    // Home button click handler - behaves like a state button
    const handleHomeClick = () => {
        closeAccessibilityModal();
        // Exit saved mode if active (selector_visibleStops handles filtering)

        // Set active state to home
        setActiveState('home');
        onStateChange?.('home');

        // Go back to start page
        setTourStage('startPage');
    };

    // Add Place button click handler - toggles the add place panel
    const handleAddPlaceClick = () => {
        closeAccessibilityModal();

        // If already active, close and return to default list mode
        if (activeState === 'addPlace') {
            setActiveState(null);
            onStateChange?.(null);
            setListMode(isDesktop ? 'desktop' : 'mobileList');
            return;
        }

        // Exit saved mode if active (selector_visibleStops handles filtering)

        // Set active state to addPlace
        setActiveState('addPlace');
        onStateChange?.('addPlace');

        // Open the add place panel (shows "Add Stop" button + user stops list)
        if (isDesktop) {
            setListMode('desktopAddPlace');
        } else {
            setListMode('mobileAddPlace');
        }
    };

    const handlePlanRouteClick = () => {
        closeAccessibilityModal();
        if (activeState === 'planRoute') {
            setActiveState(null);
            onStateChange?.(null);
            setListMode(isDesktop ? 'desktop' : 'mobileList');
            return;
        }
        setActiveState('planRoute');
        onStateChange?.('planRoute');
        try {
            const stored = localStorage.getItem('plannedRoute');
            const existingRoute: PlannedRoute | null = stored ? JSON.parse(stored) : null;
            if (existingRoute?.stopOrder?.length) {
                setPlannedRoute(existingRoute);
                setListMode('mobileUserPlannedTourResults');
                return;
            }
        } catch { }
        setListMode('mobileUserPlannedTour');
    };

    // Notifications button click handler - toggles notifications panel
    const handleNotificationsClick = () => {
        closeAccessibilityModal();
        // Only allow notifications when on map stage
        if (!isMapStage) return;

        // If already active, close and return to default list mode
        if (activeState === 'notifications') {
            setActiveState(null);
            onStateChange?.(null);
            if (isDesktop) {
                setListMode('desktop');
            }
            return;
        }

        // Exit saved mode if active (selector_visibleStops handles filtering)

        // Set active state to notifications
        setActiveState('notifications');
        onStateChange?.('notifications');

        if (isDesktop) {
            // On desktop, show the desktopNotifications panel
            setListMode('desktopNotifications');
        }
        // On mobile, the full-screen NotificationsPage is rendered in TourSummaryOverlay
        // based on sideBarState === 'notifications', no need to set listMode
    };

    // My Tours button click handler
    const handleMyToursClick = () => {
        closeAccessibilityModal();
        const raw = localStorage.getItem('credential_items');
        const credentials = raw ? JSON.parse(raw) : {};

        if (credentials.uID) {
            // Clear badge (state + persisted) — known count will be updated by MyToursPage
            setNewToursCount(0);
            localStorage.removeItem(`my_tours_new_count_${credentials.uID}`);

            const url = `/mytours/${credentials.uID}`;
            navigate(url);
        }
    };

    // Helper to create a sidebar button
    const SidebarButton = ({
        onClick,
        label,
        icon,
        isActive = false,
        badge,
        animate = false,
        inGrid = false,
    }: {
        onClick: () => void;
        label: string;
        icon: React.ReactNode;
        isActive?: boolean;
        badge?: number;
        animate?: boolean;
        inGrid?: boolean;
    }) => (
        <Tooltip title={label} placement={isDesktop ? 'right' : 'top'} arrow>
            <IconButton
                onClick={onClick}
                aria-label={label}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: isDesktop ? 75 : (inGrid ? 56 : 56),
                    paddingTop: isDesktop ? '10px' : (inGrid ? '6px' : '6px'),
                    paddingBottom: 0,
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive || animate ? tourColor : sidebarTextColor,
                    bgcolor: 'transparent',
                    transition: 'all 0.3s ease',
                    transform: animate ? 'scale(1.1)' : 'scale(1)',
                    '&:hover': {
                        bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.1)' : 'rgba(208, 85, 46, 0.08)',
                    },
                    ...(!inGrid && {
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            ...(isDesktop ? {
                                left: -1,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '4px',
                                height: '70%',
                                borderRadius: '0 4px 4px 0',
                            } : {
                                bottom: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '70%',
                                height: '3px',
                                borderRadius: '4px 4px 0 0',
                            }),
                            bgcolor: isActive ? tourColor : 'transparent',
                            transition: 'all 0.3s ease',
                        },
                    }),
                }}
            >
                {badge !== undefined && badge > 0 ? (
                    <Badge
                        badgeContent={badge}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '10px',
                                minWidth: '16px',
                                height: '16px',
                                top: -2,
                                right: -2,
                            },
                        }}
                    >
                        {icon}
                    </Badge>
                ) : icon}
                <Typography variant="caption" sx={{ mt: 0.5, fontSize: isDesktop ? '0.75rem' : '0.6rem', lineHeight: 1.1, textAlign: 'center' }}>
                    {label}
                </Typography>
            </IconButton>
        </Tooltip>
    );

    // Home button component
    const HomeButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{ width: isDesktop ? '100%' : 'auto', flex: isDesktop ? 'none' : 1, minWidth: 0 }}>
            <SidebarButton
                onClick={handleHomeClick}
                label={t('home') || 'Home'}
                isActive={activeState === 'home'}
                inGrid={inGrid}
                icon={activeState === 'home' ? <HomeIcon color={tourColor} /> : <EmptyHomeIcon color={sidebarTextColor} />}
            />
        </Box>
    );

    // Notifications button component
    const NotificationsButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{ width: isDesktop ? '100%' : 'auto', flex: isDesktop ? 'none' : 1, minWidth: 0 }}>
            <SidebarButton
                onClick={handleNotificationsClick}
                label={t('notifications') || 'Notifications'}
                isActive={activeState === 'notifications'}
                badge={unreadNotificationsCount}
                animate={animateNotifications}
                inGrid={inGrid}
                icon={
                    <i
                        className={activeState === 'notifications' || animateNotifications ? 'fa-solid fa-bell' : 'fa-regular fa-bell'}
                        style={{ fontSize: isDesktop ? '22px' : '20px', transition: 'all 0.3s ease' }}
                        aria-hidden="true"
                    />
                }
            />
        </Box>
    );

    // Add Place button component
    const AddPlaceButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{ width: isDesktop ? '100%' : 'auto', flex: isDesktop ? 'none' : 1, minWidth: 0 }}>
            <SidebarButton
                onClick={() => {
                    handleAddPlaceClick();
                    if (inGrid) setMoreExpanded(false);
                }}
                label={t('add_place') || 'Add Place'}
                isActive={activeState === 'addPlace'}
                inGrid={inGrid}
                icon={
                    <i
                        className="fa-solid fa-location-dot"
                        style={{
                            fontSize: isDesktop ? '22px' : '20px',
                            ...(activeState === 'addPlace'
                                ? {}
                                : { WebkitTextStroke: `1.5px ${sidebarTextColor}`, color: sidebarBg }
                            ),
                        }}
                        aria-hidden="true"
                    />
                }
            />
        </Box>
    );

    const PlanRouteButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{ width: isDesktop ? '100%' : 'auto', flex: isDesktop ? 'none' : 1, minWidth: 0 }}>
            <SidebarButton
                onClick={() => {
                    handlePlanRouteClick();
                    if (inGrid) setMoreExpanded(false);
                }}
                label={t('user_planned_tour.plan_route') || 'Plan Route'}
                isActive={activeState === 'planRoute'}
                inGrid={inGrid}
                icon={
                    <i
                        className="fa-solid fa-route"
                        style={{
                            fontSize: isDesktop ? '22px' : '20px',
                            ...(activeState === 'planRoute'
                                ? {}
                                : { WebkitTextStroke: `1.5px ${sidebarTextColor}`, color: sidebarBg }
                            ),
                        }}
                        aria-hidden="true"
                    />
                }
            />
        </Box>
    );

    // My Tours button component
    const MyToursButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{
            width: isDesktop ? '100%' : (inGrid ? '100%' : '20%'),
        }}>
            <SidebarButton
                onClick={() => {
                    handleMyToursClick();
                    if (inGrid) setMoreExpanded(false);
                }}
                label={t('my_tours.title') || 'My Tours'}
                badge={newToursCount}
                inGrid={inGrid}
                icon={
                    <svg
                        width="25px"
                        height="25px"
                        viewBox="18 3 20 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ minWidth: '25px', minHeight: '25px', flexShrink: 0 }}
                    >
                        <path d="M34 18.0156C35.245 18.5446 36 19.2386 36 19.9996C36 21.6566 32.418 22.9996 28 22.9996C23.582 22.9996 20 21.6566 20 19.9996C20 19.2396 20.755 18.5436 22 18.0156" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M33 10.444C33 13.537 28 19 28 19C28 19 23 13.537 23 10.444C23 7.351 25.239 5 28 5C30.761 5 33 7.352 33 10.444Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M28 11C28.5523 11 29 10.5523 29 10C29 9.44772 28.5523 9 28 9C27.4477 9 27 9.44772 27 10C27 10.5523 27.4477 11 28 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                }
            />
        </Box>
    );

    // Accessibility button component
    const AccessibilityButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box
            sx={{
                width: isDesktop ? '100%' : (inGrid ? '100%' : '20%'),
            }}
            onClick={(e) => {
                e.stopPropagation();
                setMoreExpanded(false);
            }}
        >
            <AccessibilityControls sidebarStyle isDesktop={isDesktop} />
        </Box>
    );

    // Smart Agent button click handler
    const handleSmartAgentClick = () => {
        closeAccessibilityModal();
        passiveCollector.trackChatScreenOpened();
        setShowSmartAgent(true);
        setMoreExpanded(false);
    };

    // Smart Agent button component
    const SmartAgentButton = ({ inGrid = false }: { inGrid?: boolean }) => (
        <Box sx={{
            width: isDesktop ? '100%' : (inGrid ? '100%' : '20%'),
        }}>
            <SidebarButton
                onClick={handleSmartAgentClick}
                label={t('new_app.smart_guide') || 'Smart Guide'}
                isActive={showSmartAgent}
                inGrid={inGrid}
                icon={
                    <svg
                        width={isDesktop ? "24" : "22"}
                        height={isDesktop ? "21" : "19"}
                        viewBox="0 0 25 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ minWidth: isDesktop ? '24px' : '22px', minHeight: isDesktop ? '21px' : '19px' }}
                    >
                        <path d="M22.6213 16.6558C22.4944 16.6558 22.3915 16.5552 22.3915 16.4311C22.3915 15.735 22.1355 15.0563 21.6511 14.4688C21.5716 14.3723 21.5871 14.2307 21.6859 14.1528C21.7845 14.075 21.9292 14.0901 22.009 14.1869C22.5599 14.8552 22.851 15.6312 22.851 16.4312C22.8511 16.5552 22.7482 16.6558 22.6213 16.6558Z" fill="currentColor" />
                        <path d="M3.654 5.08679C3.6053 5.08679 3.55603 5.07167 3.51441 5.04041C3.41367 4.96492 3.39469 4.82385 3.47189 4.72544C4.27477 3.70157 5.32303 2.86104 6.5032 2.29472C6.61697 2.23963 6.75487 2.28606 6.81061 2.39739C6.86658 2.50871 6.81956 2.64333 6.70561 2.69796C5.58867 3.23407 4.59666 4.02965 3.83653 4.99879C3.79134 5.0565 3.72309 5.08679 3.654 5.08679Z" fill="currentColor" />
                        <path d="M10.256 19.2776C10.8959 19.2776 11.521 19.2151 12.1266 19.0988C13.2396 20.6408 15.2822 21.6785 17.617 21.6785C19.1171 21.6785 20.5662 21.245 21.7128 20.4555L23.6096 21.1551C23.6938 21.1865 23.7893 21.1662 23.8532 21.1038C23.917 21.0415 23.9376 20.9482 23.9057 20.8657L23.1852 18.9969C23.7187 18.2172 24 17.3325 24 16.431C24 14.1646 22.2417 12.2336 19.7901 11.5005C19.8929 10.9482 19.9495 10.3804 19.9495 9.79956C19.9495 4.57337 15.6009 0.321503 10.256 0.321503C4.91109 0.321503 0.5625 4.57337 0.5625 9.79956C0.5625 11.5918 1.07648 13.3338 2.05013 14.8457L0.583172 17.9971C0.543469 18.0824 0.562078 18.1828 0.629953 18.2491C0.6975 18.3153 0.800484 18.3337 0.887672 18.2945L3.89723 16.9519C5.65973 18.4528 7.91166 19.2776 10.256 19.2776ZM23.5404 16.431C23.5404 17.2778 23.2636 18.1098 22.74 18.8371C22.6963 18.898 22.6861 18.9761 22.713 19.0457L23.2965 20.5593L21.7599 19.9925C21.6869 19.9657 21.6054 19.9767 21.5427 20.0217C20.4588 20.8003 19.0647 21.2291 17.617 21.2291C15.5137 21.2291 13.6665 20.3345 12.6153 18.9924C16.1237 18.1311 18.8772 15.3953 19.6967 11.9418C21.9387 12.6261 23.5404 14.3779 23.5404 16.431ZM1.25602 17.6363L2.52014 14.9207C2.55258 14.851 2.54648 14.7699 2.50388 14.7055C1.5345 13.2426 1.02211 11.5462 1.02211 9.79956C1.02211 4.8211 5.16441 0.770853 10.256 0.770853C15.3476 0.770853 19.4899 4.8211 19.4899 9.79956C19.4899 14.778 15.3476 18.8283 10.256 18.8283C7.97484 18.8283 5.78541 18.0084 4.09125 16.5198C4.04817 16.4819 3.99328 16.4622 3.93764 16.4622C3.90534 16.4622 3.87281 16.4689 3.84225 16.4825L1.25602 17.6363Z" fill="currentColor" />
                        <path d="M10.256 7.95894C9.32653 7.95894 8.57034 8.69832 8.57034 9.60711V14.6187C8.57034 15.5276 9.32653 16.267 10.256 16.267C11.1855 16.267 11.9417 15.5276 11.9417 14.6187V9.60715C11.9417 8.69832 11.1855 7.95894 10.256 7.95894ZM11.4822 14.6187C11.4822 15.2798 10.9321 15.8177 10.256 15.8177C9.57984 15.8177 9.02986 15.2798 9.02986 14.6187V9.60715C9.02986 8.9461 9.57984 8.40829 10.256 8.40829C10.9321 8.40829 11.4822 8.94605 11.4822 9.60715V14.6187Z" fill="currentColor" />
                        <path d="M10.2577 6.92673C11.2703 6.92673 12.0941 6.12116 12.0941 5.13112C12.0941 4.13919 11.2702 3.3322 10.2577 3.3322C9.24319 3.3322 8.41786 4.13919 8.41786 5.13112C8.41786 6.12121 9.24319 6.92673 10.2577 6.92673ZM10.2577 3.78155C11.0169 3.78155 11.6345 4.38701 11.6345 5.13112C11.6345 5.87348 11.0169 6.47738 10.2577 6.47738C9.49664 6.47738 8.87742 5.87348 8.87742 5.13112C8.87742 4.38701 9.49664 3.78155 10.2577 3.78155Z" fill="currentColor" />
                        <path d="M22.6213 16.6558C22.4944 16.6558 22.3915 16.5552 22.3915 16.4311C22.3915 15.735 22.1355 15.0563 21.6511 14.4688C21.5716 14.3723 21.5871 14.2307 21.6859 14.1528C21.7845 14.075 21.9292 14.0901 22.009 14.1869C22.5599 14.8552 22.851 15.6312 22.851 16.4312C22.8511 16.5552 22.7482 16.6558 22.6213 16.6558Z" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M3.654 5.08679C3.6053 5.08679 3.55603 5.07167 3.51441 5.04041C3.41367 4.96492 3.39469 4.82385 3.47189 4.72544C4.27477 3.70157 5.32303 2.86104 6.5032 2.29472C6.61697 2.23963 6.75487 2.28606 6.81061 2.39739C6.86658 2.50871 6.81956 2.64333 6.70561 2.69796C5.58867 3.23407 4.59666 4.02965 3.83653 4.99879C3.79134 5.0565 3.72309 5.08679 3.654 5.08679Z" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M10.256 19.2776C10.8959 19.2776 11.521 19.2151 12.1266 19.0988C13.2396 20.6408 15.2822 21.6785 17.617 21.6785C19.1171 21.6785 20.5662 21.245 21.7128 20.4555L23.6096 21.1551C23.6938 21.1865 23.7893 21.1662 23.8532 21.1038C23.917 21.0415 23.9376 20.9482 23.9057 20.8657L23.1852 18.9969C23.7187 18.2172 24 17.3325 24 16.431C24 14.1646 22.2417 12.2336 19.7901 11.5005C19.8929 10.9482 19.9495 10.3804 19.9495 9.79956C19.9495 4.57337 15.6009 0.321503 10.256 0.321503C4.91109 0.321503 0.5625 4.57337 0.5625 9.79956C0.5625 11.5918 1.07648 13.3338 2.05013 14.8457L0.583172 17.9971C0.543469 18.0824 0.562078 18.1828 0.629953 18.2491C0.6975 18.3153 0.800484 18.3337 0.887672 18.2945L3.89723 16.9519C5.65973 18.4528 7.91166 19.2776 10.256 19.2776ZM23.5404 16.431C23.5404 17.2778 23.2636 18.1098 22.74 18.8371C22.6963 18.898 22.6861 18.9761 22.713 19.0457L23.2965 20.5593L21.7599 19.9925C21.6869 19.9657 21.6054 19.9767 21.5427 20.0217C20.4588 20.8003 19.0647 21.2291 17.617 21.2291C15.5137 21.2291 13.6665 20.3345 12.6153 18.9924C16.1237 18.1311 18.8772 15.3953 19.6967 11.9418C21.9387 12.6261 23.5404 14.3779 23.5404 16.431ZM1.25602 17.6363L2.52014 14.9207C2.55258 14.851 2.54648 14.7699 2.50388 14.7055C1.5345 13.2426 1.02211 11.5462 1.02211 9.79956C1.02211 4.8211 5.16441 0.770853 10.256 0.770853C15.3476 0.770853 19.4899 4.8211 19.4899 9.79956C19.4899 14.778 15.3476 18.8283 10.256 18.8283C7.97484 18.8283 5.78541 18.0084 4.09125 16.5198C4.04817 16.4819 3.99328 16.4622 3.93764 16.4622C3.90534 16.4622 3.87281 16.4689 3.84225 16.4825L1.25602 17.6363Z" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M10.256 7.95894C9.32653 7.95894 8.57034 8.69832 8.57034 9.60711V14.6187C8.57034 15.5276 9.32653 16.267 10.256 16.267C11.1855 16.267 11.9417 15.5276 11.9417 14.6187V9.60715C11.9417 8.69832 11.1855 7.95894 10.256 7.95894ZM11.4822 14.6187C11.4822 15.2798 10.9321 15.8177 10.256 15.8177C9.57984 15.8177 9.02986 15.2798 9.02986 14.6187V9.60715C9.02986 8.9461 9.57984 8.40829 10.256 8.40829C10.9321 8.40829 11.4822 8.94605 11.4822 9.60715V14.6187Z" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M10.2577 6.92673C11.2703 6.92673 12.0941 6.12116 12.0941 5.13112C12.0941 4.13919 11.2702 3.3322 10.2577 3.3322C9.24319 3.3322 8.41786 4.13919 8.41786 5.13112C8.41786 6.12121 9.24319 6.92673 10.2577 6.92673ZM10.2577 3.78155C11.0169 3.78155 11.6345 4.38701 11.6345 5.13112C11.6345 5.87348 11.0169 6.47738 10.2577 6.47738C9.49664 6.47738 8.87742 5.87348 8.87742 5.13112C8.87742 4.38701 9.49664 3.78155 10.2577 3.78155Z" stroke="currentColor" strokeWidth="0.5" />
                    </svg>
                }
            />
        </Box>
    );

    // More button for mobile
    const MoreButton = () => (
        <Box sx={{ flex: 1, minWidth: 0, }}>
            <Tooltip title={t('more') || 'More'} placement="top" arrow>
                <IconButton
                    onClick={() => setMoreExpanded(!moreExpanded)}
                    aria-label={t('more') || 'More'}
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: 56,
                        paddingX: '4px',
                        marginX: '4px',
                        paddingTop: '6px',
                        paddingBottom: 0,
                        borderRadius: '12px',
                        display: 'flex',
                        border: 'none',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: moreExpanded ? tourColor : (newToursCount > 0 ? '#d32f2f' : sidebarTextColor),
                        bgcolor: 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.1)' : 'rgba(208, 85, 46, 0.08)',
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '70%',
                            height: '3px',
                            borderRadius: '4px 4px 0 0',
                            bgcolor: moreExpanded ? tourColor : 'transparent',
                            transition: 'all 0.3s ease',
                        },
                    }}
                >
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <i
                            className="fa-solid fa-ellipsis"
                            style={{ fontSize: '20px', transition: 'all 0.3s ease' }}
                            aria-hidden="true"
                        />
                        {newToursCount > 0 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -5,
                                    right: -9,
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    bgcolor: '#d32f2f',
                                }}
                            />
                        )}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.6rem', lineHeight: 1.1 }}>
                        {t('more') || 'More'}
                    </Typography>
                </IconButton>
            </Tooltip>
        </Box>
    );


    // Desktop sidebar content
    const desktopSidebarContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '85px',
                height: '100%',
                bgcolor: sidebarBg,
            }}
        >
            <HomeButton />
            <SavedButton onStateChange={onStateChange} isDesktop={isDesktop} />
            <AddPlaceButton />
            {/* DISABLED FOR PROD — re-enable for next release: planned-route feature */}
            {/* <PlanRouteButton /> */}
            <NotificationsButton />
            <SmartAgentButton />
            <MyToursButton />
            <AccessibilityButton />
        </Box>
    );

    // Mobile sidebar content with "More" button - Main bar fixed, expanded section floating
    const mobileSidebarContent = (
        <>
            {/* Main bar - fixed at bottom, 100% width */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: Z_INDEX.sidebar + 2,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0,
                    alignItems: 'center',
                    justifyContent: 'stretch',
                    width: '100%',
                    height: '60px',
                    bgcolor: sidebarBg,
                    boxShadow: moreExpanded ? 'none' : '0 -2px 10px rgba(0,0,0,0.1)',
                    borderTop: 'none',
                    transition: 'box-shadow 0.3s ease',
                }}
            >
                <MoreButton />
                <HomeButton />
                <SavedButton onStateChange={onStateChange} isDesktop={isDesktop} mobileWidth="20%" />
                <AddPlaceButton />
                <NotificationsButton />
            </Box>

            {/* Backdrop */}
            {moreExpanded && (
                <Box
                    onClick={() => setMoreExpanded(false)}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: Z_INDEX.sidebar,
                        bgcolor: 'rgba(0,0,0,0.3)',
                    }}
                />
            )}

            {/* Menu panel */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: '60px',
                    left: 0,
                    right: 0,
                    zIndex: Z_INDEX.sidebar + 1,
                    bgcolor: sidebarBg,
                    borderRadius: 0,
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
                    transform: moreExpanded ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Menu items */}
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 2 }}>
                    {/* Smart Guide */}
                    <Box
                        onClick={handleSmartAgentClick}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 2,
                            cursor: 'pointer',
                            color: showSmartAgent ? tourColor : sidebarMenuTextColor,
                            '&:active': { bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        <svg width="24" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                            <path d="M10.256 19.2776C10.8959 19.2776 11.521 19.2151 12.1266 19.0988C13.2396 20.6408 15.2822 21.6785 17.617 21.6785C19.1171 21.6785 20.5662 21.245 21.7128 20.4555L23.6096 21.1551C23.6938 21.1865 23.7893 21.1662 23.8532 21.1038C23.917 21.0415 23.9376 20.9482 23.9057 20.8657L23.1852 18.9969C23.7187 18.2172 24 17.3325 24 16.431C24 14.1646 22.2417 12.2336 19.7901 11.5005C19.8929 10.9482 19.9495 10.3804 19.9495 9.79956C19.9495 4.57337 15.6009 0.321503 10.256 0.321503C4.91109 0.321503 0.5625 4.57337 0.5625 9.79956C0.5625 11.5918 1.07648 13.3338 2.05013 14.8457L0.583172 17.9971C0.543469 18.0824 0.562078 18.1828 0.629953 18.2491C0.6975 18.3153 0.800484 18.3337 0.887672 18.2945L3.89723 16.9519C5.65973 18.4528 7.91166 19.2776 10.256 19.2776Z" stroke="currentColor" strokeWidth="1.2" fill="none" />
                            <path d="M10.256 7.95894C9.32653 7.95894 8.57034 8.69832 8.57034 9.60711V14.6187C8.57034 15.5276 9.32653 16.267 10.256 16.267C11.1855 16.267 11.9417 15.5276 11.9417 14.6187V9.60715C11.9417 8.69832 11.1855 7.95894 10.256 7.95894Z" stroke="currentColor" strokeWidth="1" fill="none" />
                            <circle cx="10.26" cy="5.13" r="1.8" stroke="currentColor" strokeWidth="1" fill="none" />
                        </svg>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                            {t('new_app.smart_guide') || 'Smart Guide'}
                        </Typography>
                    </Box>

                    {/* DISABLED FOR PROD — re-enable for next release: planned-route feature */}
                    {/* Plan Route */}
                    {/*
                    <Box
                        onClick={() => { handlePlanRouteClick(); setMoreExpanded(false); }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 2,
                            cursor: 'pointer',
                            color: activeState === 'planRoute' ? tourColor : sidebarMenuTextColor,
                            '&:active': { bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        <i className="fa-solid fa-route" aria-hidden="true" style={{ fontSize: '22px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                            {t('user_planned_tour.plan_route') || 'Plan Route'}
                        </Typography>
                    </Box>
                    */}

                    {/* My Tours */}
                    <Box
                        onClick={() => { handleMyToursClick(); setMoreExpanded(false); }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 2,
                            cursor: 'pointer',
                            color: sidebarMenuTextColor,
                            '&:active': { bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        {newToursCount > 0 ? (
                            <Badge
                                badgeContent={newToursCount}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '10px',
                                        minWidth: '16px',
                                        height: '16px',
                                        top: -2,
                                        right: -2,
                                    },
                                }}
                            >
                                <svg width="24" height="24" viewBox="18 3 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                    <path d="M34 18.0156C35.245 18.5446 36 19.2386 36 19.9996C36 21.6566 32.418 22.9996 28 22.9996C23.582 22.9996 20 21.6566 20 19.9996C20 19.2396 20.755 18.5436 22 18.0156" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M33 10.444C33 13.537 28 19 28 19C28 19 23 13.537 23 10.444C23 7.351 25.239 5 28 5C30.761 5 33 7.352 33 10.444Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M28 11C28.5523 11 29 10.5523 29 10C29 9.44772 28.5523 9 28 9C27.4477 9 27 9.44772 27 10C27 10.5523 27.4477 11 28 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Badge>
                        ) : (
                            <svg width="24" height="24" viewBox="18 3 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                <path d="M34 18.0156C35.245 18.5446 36 19.2386 36 19.9996C36 21.6566 32.418 22.9996 28 22.9996C23.582 22.9996 20 21.6566 20 19.9996C20 19.2396 20.755 18.5436 22 18.0156" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M33 10.444C33 13.537 28 19 28 19C28 19 23 13.537 23 10.444C23 7.351 25.239 5 28 5C30.761 5 33 7.352 33 10.444Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M28 11C28.5523 11 29 10.5523 29 10C29 9.44772 28.5523 9 28 9C27.4477 9 27 9.44772 27 10C27 10.5523 27.4477 11 28 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                            {t('my_tours.title') || 'My Tours'}
                        </Typography>
                    </Box>

                    {/* Accessibility */}
                    <Box
                        onClick={() => {
                            setModalType('accessibility');
                            setShowModal(true);
                            setMoreExpanded(false);
                        }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 2,
                            cursor: 'pointer',
                            color: sidebarMenuTextColor,
                            '&:active': { bgcolor: sidebarIsDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        <i
                            className="fa-regular fa-universal-access"
                            aria-hidden="true"
                            style={{ fontSize: '22px', flexShrink: 0 }}
                        />
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                            {t('accessibility.title', 'Accessibility')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </>
    );

    const sidebarContent = isDesktop ? desktopSidebarContent : mobileSidebarContent;

    // On mobile, use Portal to render at document.body level for complete separation
    if (!isDesktop) {
        const portalTarget = document.querySelector('.App') || document.body;
        return ReactDOM.createPortal(sidebarContent, portalTarget);
    }

    // On desktop, render normally as part of the layout
    return sidebarContent;
});
